<template>
  <div class="case-forms-library">
    <p v-if="loadError" class="load-error" role="alert" data-test="load-error">
      {{ t('system_admin.case_forms_library.index.load_error') }}
    </p>
    <p v-if="toggleError" class="load-error" role="alert" data-test="toggle-error">
      {{ t('system_admin.case_forms_library.index.toggle_error') }}
    </p>

    <data-grid-remote
      ref="grid"
      :total-rows="fetchTotalRows"
      :server-url="serverUrl"
      :data-fetch-callback-fn="dataFetchCallbackFn"
      :fetchOptions="fetchOptions"
      :useFetchOptions="useFetchOptions"
      :pageSize="pageSize"
      :currentPage="currentPage"
      :paginationOptions="paginationOptions"
      :noResultsMessage="t('system_admin.case_forms_library.index.no_results')"
      row-key="row_key"
      @update:currentPage="onPageUpdate"
      @update:pageSize="onPageSizeUpdate"
      @update:sort="onSortChange"
      data-test="remote-grid"
    >
      <template v-slot:header>
        <div class="filters-container">
          <form-input
            :model-value="searchInput"
            @update:model-value="onSearchInput"
            name="search"
            type="search"
            :label="t('system_admin.case_forms_library.index.search_label')"
            :placeholder="t('system_admin.case_forms_library.index.search_placeholder')"
            hide-label
            data-test="search-input"
          />

          <f-filter-bar v-model="filterValue" data-test="filter-bar">
            <f-filter-bar-multiselect
              :title="t('system_admin.case_forms_library.index.filters.shareable')"
              name="shareable"
            >
              <f-filter-bar-multiselect-option :title="t('system_admin.case_forms_library.index.shareable')" value="true" />
              <f-filter-bar-multiselect-option :title="t('system_admin.case_forms_library.index.not_shareable')" value="false" />
            </f-filter-bar-multiselect>

            <f-filter-bar-multiselect
              :title="t('system_admin.case_forms_library.index.filters.case_type')"
              name="case_type_ids"
            >
              <f-filter-bar-multiselect-option
                v-for="caseType in caseTypes"
                :key="caseType.id"
                :title="caseType.name"
                :value="String(caseType.id)"
              />
            </f-filter-bar-multiselect>
          </f-filter-bar>
        </div>
      </template>

      <data-grid-column
        :title="t('system_admin.case_forms_library.index.columns.name')"
        data-id="name"
        sortable
        v-slot="value"
      >
        {{ value }}
      </data-grid-column>

      <data-grid-column
        :title="t('system_admin.case_forms_library.index.columns.case_type')"
        data-id="case_type_name"
        sortable
        v-slot="value"
      >
        {{ value }}
      </data-grid-column>

      <data-grid-column
        :title="t('system_admin.case_forms_library.index.columns.shareable')"
        data-id="shareable"
        :cell-formatter="(_value, row) => row"
        v-slot="row"
      >
        <f-checkbox
          :model-value="shareableByFormId[row.custom_form_id]"
          @update:model-value="(value) => toggleShareable(row, value)"
          :name="`shareable-${row.row_key}`"
          :label="t('system_admin.case_forms_library.index.toggle_label', { name: row.name })"
          :disabled="pendingFormIds.has(row.custom_form_id)"
          is-switch
          :data-test="`shareable-toggle-${row.row_key}`"
        />
      </data-grid-column>

      <data-grid-column
        :title="t('system_admin.case_forms_library.index.columns.view')"
        data-id="view_path"
        v-slot="value"
      >
        <a
          class="view-link"
          :href="value"
          target="_blank"
          rel="noopener noreferrer"
          data-test="view-link"
        >
          <svg-icon icon="password-reveal" :width="16" :height="16" aria-hidden="true" />
          <span>{{ t('system_admin.case_forms_library.index.view') }}</span>
        </a>
      </data-grid-column>
    </data-grid-remote>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import {
  DataGridColumn,
  DataGridRemote,
  FCheckbox,
  FFilterBar,
  FFilterBarMultiselect,
  FFilterBarMultiselectOption,
  FormInput,
  SvgIcon,
} from '@raptor/feather-ui';
import { csrf } from '~/helpers/application_helpers.js';

const { t } = I18n;

const SEARCH_DEBOUNCE_MS = 300;

const props = defineProps({
  dataUrl: { type: String, required: true },
  toggleUrlTemplate: { type: String, required: true },
  caseTypes: { type: Array, default: () => [] },
});

// Search and filters
const searchInput = ref('');
const search = ref('');
let searchTimer;
const onSearchInput = (value) => {
  searchInput.value = value;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => { search.value = value.trim(); }, SEARCH_DEBOUNCE_MS);
};
onBeforeUnmount(() => clearTimeout(searchTimer));

const filterValue = ref({});
const shareableFilter = computed(() => {
  const selected = filterValue.value.shareable || [];
  return selected.length === 1 ? selected[0] : undefined;
});

const filtersForRequest = computed(() => ({
  search: search.value,
  case_type_ids: filterValue.value.case_type_ids || [],
  shareable: shareableFilter.value,
}));

// Pagination
const paginationOptions = { perPageOptions: [10, 25, 50] };
const pageSize = ref(10);
const currentPage = ref(1);
const onPageSizeUpdate = (newPageSize) => { pageSize.value = newPageSize; };
const onPageUpdate = (newPageNumber) => { currentPage.value = newPageNumber; };
watch(filtersForRequest, () => { currentPage.value = 1; }, { deep: true });

// Sorting
const sortBy = ref();
const sortOrder = ref();
const onSortChange = (sorts) => {
  const primarySort = sorts.at(-1);
  sortBy.value = primarySort?.dataId;
  sortOrder.value = primarySort?.direction;
  currentPage.value = 1;
};

const totalRows = ref(0);
const fetchTotalRows = computed(() => () => totalRows.value);

// Fetch
const grid = ref();
const loadError = ref(false);
const serverUrl = () => props.dataUrl;

// Ordering marks so a grid response that started before a toggle cannot undo it
let clock = 0;
let lastFetchStartedAt = 0;
const toggledAt = {};

const useFetchOptions = {
  beforeFetch({ options }) {
    lastFetchStartedAt = ++clock;
    options.headers = {
      ...options.headers,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrf(),
    };
    return { options };
  },
  afterFetch(ctx) {
    loadError.value = false;
    toggleError.value = false;
    return ctx;
  },
  onFetchError(ctx) {
    loadError.value = true;
    totalRows.value = 0;
    return ctx;
  },
};

const fetchOptions = computed(() => ({
  method: 'post',
  body: {
    page: currentPage.value,
    per_page: pageSize.value,
    filters: filtersForRequest.value,
    sort_by: sortBy.value,
    sort_order: sortOrder.value,
  },
}));

// Shareable state is per form; a form attached to several case types has one row per case type
const shareableByFormId = ref({});
const pendingFormIds = ref(new Set());
const toggleError = ref(false);

const toggleShareable = async (row, shareable) => {
  const formId = row.custom_form_id;
  const previous = shareableByFormId.value[formId];
  shareableByFormId.value[formId] = shareable;
  pendingFormIds.value.add(formId);
  toggledAt[formId] = ++clock;
  toggleError.value = false;

  try {
    const response = await fetch(props.toggleUrlTemplate.replace('__id__', formId), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf() },
      body: JSON.stringify({ shareable }),
    });
    // Fetches that started before the save settled may still hold the old value
    toggledAt[formId] = ++clock;
    if (!response.ok) throw new Error(`Toggle failed with ${response.status}`);

    const payload = await response.json();
    shareableByFormId.value[formId] = payload.shareable;
    // The form may no longer match the shareable filter, so reload the page of results
    if (shareableFilter.value !== undefined) grid.value?.refreshFn();
  } catch (error) {
    console.error('Shareable toggle failed', error);
    toggledAt[formId] = ++clock;
    shareableByFormId.value[formId] = previous;
    toggleError.value = true;
  } finally {
    pendingFormIds.value.delete(formId);
  }
};

const dataFetchCallbackFn = (payload) => {
  if (!payload?.data) return [];

  totalRows.value = payload.total || 0;
  return payload.data.map((form) => {
    const formId = form.custom_form_id;
    const toggledSinceFetch = pendingFormIds.value.has(formId) || (toggledAt[formId] || 0) > lastFetchStartedAt;
    if (!toggledSinceFetch) shareableByFormId.value[formId] = form.shareable;
    return { ...form, row_key: `${form.custom_form_id}-${form.case_type_id}` };
  });
};
</script>

<style lang="scss" scoped>
@import "@raptor/feather-ui/_base.scss";

.filters-container {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.load-error {
  color: #e12336;
}

.view-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: $f-link;
  fill: $f-link;

  span {
    text-decoration: underline;
  }
}

// cpoms global input styles override feather's field control
:deep(.field__control) {
  max-width: 265px;
}

// feather sets cells to 1.14286rem via `.vue-container .data-grid_body td.data-grid-td`
.case-forms-library :deep(.data-grid .data-grid_body td.data-grid-td) {
  font-size: 14px;
}

// cpoms' `.vue-container div` reset stacks feather's static filter chips
:deep(.static-bar) {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

// FCheckbox requires a label; keep it for screen readers only
:deep(.simple-checkbox--switch label) {
  font-size: 0;
  color: transparent;
  white-space: nowrap;
  overflow: hidden;
}

@media (max-width: 767px){
  :deep(.f-filter-bar) {
    display: block !important;
  }

  :deep(.filter-button),
  :deep(.active-bar-button) {
    margin-left: 0 !important;
    margin-right: 8px !important;
  }

  :deep(.clear-all-btn) {
    padding-left: 0 !important;
  }
}
</style>
